import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Curcumin is one of the most-studied options.</strong>
      <span className="block text-emerald-800/80 mt-0.5">BCM-95® uses the patented BCM-95<sup>&reg;</sup> extract. £37.99 first-bottle welcome offer.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors">
      Shop BCM-95® <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const sections = [
  {
    id: 'context',
    heading: 'Why UK adults are paying attention to joint health',
    body: (
      <>
        <p>The UK population is ageing, and active-living habits — running, padel, yoga, cycling, weight training, hiking — are now mainstream into the 40s, 50s and beyond. National survey data from the Office for National Statistics and Sport England consistently shows that everyday mobility and <em>staying able to do the things you enjoy</em> sit at the top of the "healthy ageing" priority list.</p>
        <p>This article is a UK-focused, regulation-aware buyer's guide. We do not promise relief from any specific medical condition — that's a job for your GP. We do explain what kinds of food supplements UK consumers commonly explore as part of a broader joint-comfort and active-lifestyle routine, and what to look for on a label.</p>
        <p className="text-[13px] italic text-slate-500">Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Not intended to diagnose, treat, cure or prevent any disease. If you have a diagnosed joint condition, please speak to your GP.</p>
      </>
    ),
  },
  {
    id: 'categories',
    heading: 'The main categories on the UK market',
    body: (
      <>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Curcumin / turmeric extracts</strong> — polyphenol-rich, often used as part of joint-comfort and active-recovery routines. The most-studied formulations include BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup>, Meriva, Theracurmin and Longvida.</li>
          <li><strong>Glucosamine &amp; Chondroitin</strong> — long-standing structural supplements aimed at cartilage support.</li>
          <li><strong>Marine collagen / hydrolysed collagen peptides</strong> — type II collagen is the most-studied for joint support.</li>
          <li><strong>Omega-3 (EPA/DHA)</strong> — typically high-quality fish oil or algal oil. The EFSA-authorised claim for EPA &amp; DHA relates to heart, brain and vision; many active adults also include omega-3 for broader inflammatory-balance support.</li>
          <li><strong>MSM (methylsulfonylmethane)</strong> — an organosulfur compound often combined with glucosamine.</li>
          <li><strong>Vitamin D3</strong> — Public Health England recommends 10 µg/day for all UK adults during autumn and winter; relevant to musculoskeletal function.</li>
          <li><strong>Magnesium &amp; potassium</strong> — for muscle function, especially around exercise.</li>
        </ul>
        <p>Most active adults end up with a small core stack — typically Vitamin D, Omega-3, and one or two targeted polyphenol or structural supplements — rather than a sprawling cabinet of 20 pills a day.</p>
      </>
    ),
  },
  {
    id: 'curcumin-deep-dive',
    heading: 'Why curcumin in particular?',
    body: (
      <>
        <p>Curcumin (from turmeric, <em>Curcuma longa</em>) is one of the most-studied plant polyphenols of the last 30 years. It is widely explored in active-living and joint-comfort routines. The catch is that <strong>raw curcumin is famously poorly absorbed</strong>, so the formulation matters more than the headline dose on the front of the bottle.</p>
        <p>Patented formulations such as <strong>BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup></strong> (the extract used in BCM-95®) address this by reintegrating standardised curcuminoids with turmeric's own essential oils. The advantage is a clean, single-plant supplement with no piperine — useful for anyone on medications metabolised by CYP3A4.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'how-to-pick',
    heading: 'How to pick a joint-comfort supplement (UK buyer\'s checklist)',
    body: (
      <>
        <ol className="list-decimal pl-6 space-y-2">
          <li><strong>Is the active dose clearly stated?</strong> — e.g. "650 mg curcumin per 2 capsules", not "turmeric blend 500 mg".</li>
          <li><strong>Patented or clinically-studied extract?</strong> — look for a named branded extract with published pharmacokinetic data.</li>
          <li><strong>UK food business operator?</strong> — registered UK company, real address, VAT number on invoices.</li>
          <li><strong>MHRA/ASA-compliant labelling.</strong> — UK food supplements <em>cannot</em> claim to cure, treat or prevent disease. If the marketing leans heavily on medical language, walk away.</li>
          <li><strong>Clean-label.</strong> — vegan / vegetarian capsule, allergen-free, no synthetic colours.</li>
          <li><strong>GMP-certified manufacture.</strong> — proof of batch-to-batch quality.</li>
          <li><strong>Returns policy.</strong> — at least 14 days, per UK Consumer Contracts Regulations 2013.</li>
          <li><strong>Repeat-purchase friendly pricing.</strong> — all-inclusive of UK VAT and delivery, ideally with a subscription option if you intend to take it daily long-term.</li>
        </ol>
      </>
    ),
  },
  {
    id: 'lifestyle',
    heading: 'Food supplements are only part of the picture',
    body: (
      <>
        <p>The single highest-impact intervention for long-term joint comfort and mobility is and always will be <strong>consistent movement</strong>. Strength training 2–3× per week, regular low-impact cardio, mobility drills, plenty of sleep, hydration and a Mediterranean-style diet rich in oily fish, vegetables, olive oil and polyphenols, all do more than any supplement.</p>
        <p>A curcumin food supplement like BCM-95® is best thought of as <em>one supporting layer</em> on top of an already-active lifestyle — not a shortcut.</p>
      </>
    ),
  },
  {
    id: 'faq-joint',
    heading: 'UK buyer FAQ',
    body: (
      <>
        <p className="font-semibold text-emerald-900">Are joint supplements regulated by the MHRA in the UK?</p>
        <p>Food supplements are regulated as foods, not medicines, by the Food Standards Agency and local authority Trading Standards. They must not make medical claims. Only authorised health claims under the GB Nutrition &amp; Health Claims Register may appear on labels.</p>
        <p className="font-semibold text-emerald-900">Can I take more than one joint supplement together?</p>
        <p>Most curcumin, glucosamine and omega-3 supplements are well tolerated together, but the safest approach is to introduce them one at a time over a few weeks, and to speak to your GP or pharmacist if you take prescription medication.</p>
        <p className="font-semibold text-emerald-900">How long until I feel a difference?</p>
        <p>Most published trials run for 8–12 weeks of consistent daily use. Be patient and avoid jumping between products every fortnight.</p>
        <p className="font-semibold text-emerald-900">Does it ship across the UK?</p>
        <p>Yes — BCM-95® ships UK-wide via Royal Mail 48 tracked delivery, with all-inclusive pricing of UK VAT and delivery.</p>
      </>
    ),
  },
];

export default function JointHealthSupplementsUK() {
  return (
    <LandingArticle
      title="Joint Health Supplements UK: 2026 Buyer's Guide | IIY Phytoventra"
      description="A UK-focused, MHRA-aware buyer's guide to joint-comfort food supplements: curcumin (BCM-95®), glucosamine, collagen, omega-3, vitamin D. What to look for on the label."
      pathOverride="/joint-health-supplements-uk"
      heading="Joint Health Supplements UK: A 2026 Buyer's Guide"
      subheading="Curcumin, glucosamine, collagen, omega-3 — what the active UK adult actually needs to know before picking a joint-comfort food supplement. MHRA / ASA aware. Plain-English."
      tag="UK Buyer's Guide"
      datePublished="2026-02-10"
      sections={sections}
    />
  );
}
